import { useCallback, useEffect, useRef, useState } from 'react';
import { toErrorMessage } from '@reflex/plugin-api/errors';
import { useAudioTranscription } from './useAudioTranscription';

/**
 * Subset of the Web Speech API types we rely on. The spec uses the vendor-prefixed
 * `webkitSpeechRecognition` on Safari/Chrome mobile, and the unprefixed
 * `SpeechRecognition` elsewhere. We only type the fields we actually use.
 */
interface SpeechRecognitionAlternative {
  transcript: string;
}
interface SpeechRecognitionResult {
  isFinal: boolean;
  0: SpeechRecognitionAlternative;
  length: number;
}
interface SpeechRecognitionResultList {
  length: number;
  [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}
interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message?: string;
}
interface SpeechRecognitionLike extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((ev: SpeechRecognitionEvent) => void) | null;
  onerror: ((ev: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export interface UseSpeechRecognitionOptions {
  /** BCP-47 language tag. Defaults to the browser language. */
  lang?: string;
  /** Fires whenever a new final chunk is recognized. */
  onFinalTranscript?: (text: string) => void;
  /** Fires with the current interim (non-final) transcript. Empty when nothing is pending. */
  onInterimTranscript?: (text: string) => void;
  /** Fires when recognition errors out. */
  onError?: (error: string) => void;
}

export interface UseSpeechRecognitionResult {
  /** True if the browser exposes a SpeechRecognition API. */
  supported: boolean;
  /** True while recognition is actively listening. */
  listening: boolean;
  /** Latest error message, if any. Cleared on start(). */
  error: string | null;
  start: () => void;
  stop: () => void;
  toggle: () => void;
}

/**
 * Turn a voice-input error into something worth showing the user, or `null`
 * when it is routine noise. The Web Speech API reports `no-speech` whenever a
 * pause outlasts its own timeout and `aborted` on every deliberate stop —
 * surfacing either would put an error under the composer during normal use.
 *
 * Anything unrecognized is passed through: the server-side fallback already
 * emits full sentences (a missing provider key, a blocked microphone), and
 * dropping those would leave a mic that silently does nothing.
 */
export function voiceInputErrorMessage(error: string | null): string | null {
  if (!error) return null;
  switch (error) {
    case 'no-speech':
    case 'aborted':
      return null;
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone access was blocked. Allow it in your browser to use voice input.';
    case 'audio-capture':
      return 'No microphone was found.';
    case 'network':
      return 'Voice input lost its connection to the speech service.';
    default:
      return error;
  }
}

/**
 * Wraps the browser Web Speech API (`SpeechRecognition` / `webkitSpeechRecognition`).
 *
 * Returns `supported: false` when the API is unavailable (e.g. Firefox, older browsers,
 * or non-browser environments). {@link useSpeechRecognition} falls back to
 * server-side transcription in that case; direct callers should hide the mic
 * UI rather than showing a disabled button.
 */
function useNativeSpeechRecognition(
  options: UseSpeechRecognitionOptions = {},
): UseSpeechRecognitionResult {
  const { lang, onFinalTranscript, onInterimTranscript, onError } = options;

  const Ctor = getSpeechRecognitionCtor();
  const supported = Ctor !== null;

  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);

  // Keep callbacks fresh without recreating the recognition instance.
  const onFinalRef = useRef(onFinalTranscript);
  const onInterimRef = useRef(onInterimTranscript);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onFinalRef.current = onFinalTranscript;
    onInterimRef.current = onInterimTranscript;
    onErrorRef.current = onError;
  }, [onFinalTranscript, onInterimTranscript, onError]);

  useEffect(() => {
    return () => {
      const r = recognitionRef.current;
      if (r) {
        r.onresult = null;
        r.onerror = null;
        r.onend = null;
        try {
          r.abort();
        } catch {
          // ignore
        }
        recognitionRef.current = null;
      }
    };
  }, []);

  const start = useCallback(() => {
    if (!Ctor || recognitionRef.current) return;
    const recognition = new Ctor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang ?? (typeof navigator !== 'undefined' ? navigator.language : 'en-US');

    recognition.onresult = (event) => {
      let finalText = '';
      let interimText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0]?.transcript ?? '';
        if (result.isFinal) {
          finalText += transcript;
        } else {
          interimText += transcript;
        }
      }
      if (finalText) onFinalRef.current?.(finalText);
      onInterimRef.current?.(interimText);
    };

    recognition.onerror = (event) => {
      const message = event.error || 'unknown';
      setError(message);
      onErrorRef.current?.(message);
    };

    recognition.onend = () => {
      setListening(false);
      recognitionRef.current = null;
      onInterimRef.current?.('');
    };

    recognitionRef.current = recognition;
    setError(null);
    try {
      recognition.start();
      setListening(true);
    } catch (err) {
      setListening(false);
      recognitionRef.current = null;
      const message = toErrorMessage(err);
      setError(message);
      onErrorRef.current?.(message);
    }
  }, [Ctor, lang]);

  const stop = useCallback(() => {
    const r = recognitionRef.current;
    if (!r) return;
    try {
      r.stop();
    } catch {
      // ignore
    }
  }, []);

  const toggle = useCallback(() => {
    if (recognitionRef.current) stop();
    else start();
  }, [start, stop]);

  return { supported, listening, error, start, stop, toggle };
}

/**
 * Voice input for the composer, on whichever engine the browser can offer.
 *
 * Chrome and Safari get the Web Speech API exactly as before: local, free,
 * and streaming interim words as they are recognized. Firefox has no such
 * API, so it records the microphone and posts each utterance to the chat
 * plugin's transcription route instead (see {@link useAudioTranscription}).
 * Both engines expose the same result shape, so a caller only ever sees
 * "voice input works" or `supported: false`.
 *
 * `supported` is true immediately on the native engine, and on the fallback
 * only after the org is confirmed to have a provider key that can transcribe
 * — the mic button appears when it can actually do something.
 */
export function useSpeechRecognition(
  options: UseSpeechRecognitionOptions = {},
): UseSpeechRecognitionResult {
  const native = useNativeSpeechRecognition(options);
  // Hooks are unconditional; `enabled` is what keeps the fallback inert (and
  // off the network) on a browser whose native recognizer already works.
  const fallback = useAudioTranscription({ ...options, enabled: !native.supported });
  return native.supported ? native : fallback;
}
